import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CircularProgress,
  TablePagination,
  Paper
} from '@mui/material';
import { PageLayout } from '../components/ui/PageLayout';
import { useSnackbar } from '../contexts/SnackbarContext';

import { Document, DocumentSortBy, SearchResult } from '../api/ApiClient';
import DocumentPermissions from '../components/documents/DocumentPermissions';
import DocumentSources from '../components/documents/DocumentSources';
import { apiClient } from '../api';
import { log } from '../utils/errors/logger';
import AuthService from '../services/AuthService';

// New modular components
import {
  DocumentSearchBar,
  DocumentTabs,
  DocumentTable,
  DocumentDialog,
  DeleteConfirmationDialog,
  DocumentFilterBar,
  DateRangeFilters
} from '../components/documents';

import { 
  useDocuments, 
  useCreateDocument,
  useUploadDocument,
  useUpdateDocument,
  useDeleteDocument
} from '../hooks/api/useDocuments';

const Documents: React.FC = () => {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning, showInfo } = useSnackbar();
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter state
  const [visibility, setVisibility] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');
  const [dateFilters, setDateFilters] = useState<DateRangeFilters>({
    createdFrom: null,
    createdTo: null,
    updatedFrom: null,
    updatedTo: null
  });
  const [sharedWithGroup, setSharedWithGroup] = useState<string>('');
  const [sharedWithUser, setSharedWithUser] = useState<string>('');

  // Get current user
  const user = AuthService.getUser();
  const canAddCustomDocs = Boolean(user);

  // Get source type filter based on tab value
  const getSourceTypeFilter = () => {
    if (tabValue === 3) {return 'sap';}
    if (tabValue === 4) {return 'custom';}
    return undefined; // All documents
  };

  // Get owner ID filter based on tab value
  const getOwnerIdFilter = () => {
    if (tabValue === 1) {return user?.id;} // My Documents tab
    return undefined;
  };

  // Get exclude_owned filter based on tab value
  const getExcludeOwnedFilter = () => {
    if (tabValue === 2) {return true;} // Shared with Me tab
    return undefined;
  };

  // Convert dates to ISO format for API
  const formatDateForAPI = (date: Date | null): string | undefined => {
    if (!date) {return undefined;}
    return date.toISOString();
  };

  // TanStack Query hooks with server-side pagination and filtering
  const {
    data,
    isLoading: documentsLoading,
    error: documentsError,
    refetch: refetchDocuments
  } = useDocuments({
    skip: page * rowsPerPage,
    limit: rowsPerPage,
    source_type: getSourceTypeFilter(),
    visibility: visibility !== 'all' ? visibility : undefined,
    sort_by: sortBy as DocumentSortBy,
    created_at_gte: formatDateForAPI(dateFilters.createdFrom),
    created_at_lte: formatDateForAPI(dateFilters.createdTo),
    updated_at_gte: formatDateForAPI(dateFilters.updatedFrom),
    updated_at_lte: formatDateForAPI(dateFilters.updatedTo),
    shared_with_group: sharedWithGroup || undefined,
    shared_with_user: sharedWithUser || undefined,
    owner_id: getOwnerIdFilter(),
    exclude_owned: getExcludeOwnedFilter()
  });

  const documents = data?.documents || [];
  const totalDocuments = data?.total || 0;
  
  // Mutations
  const createDocumentMutation = useCreateDocument();
  const uploadDocumentMutation = useUploadDocument();
  const updateDocumentMutation = useUpdateDocument();
  const deleteDocumentMutation = useDeleteDocument();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDoc, setEditingDoc] = useState<Document | null>(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteDocId, setDeleteDocId] = useState<string | null>(null);
  const [openPermissionsDialog, setOpenPermissionsDialog] = useState(false);
  const [permissionsDocId, setPermissionsDocId] = useState<string>('');
  const [permissionsDocTitle, setPermissionsDocTitle] = useState<string>('');

  // Provider state
  const [availableProviders, setAvailableProviders] = useState<Array<{ value: string; label: string }>>([
    { value: 'local', label: 'Local' }
  ]);
  const [defaultProvider, setDefaultProvider] = useState<string>('local');

  // Helper function to check if user can edit a specific document
  const canEditDocument = (doc: Document | SearchResult) => {
    if (!user) {return false;}
    // Users can edit their own documents (custom, text, etc.) but not SAP documentation
    // Admins CANNOT edit other users' private documents
    const isOwner = 'user_id' in doc && doc.user_id === user.id;
    const isSap = 'source_type' in doc && doc.source_type === 'sap';
    return !isSap && isOwner;
  };

  // Helper function to check if user can delete a specific document
  const canDeleteDocument = (doc: Document | SearchResult) => {
    if (!user) {return false;}
    // Users can delete their own documents (custom, text, etc.) but not SAP documentation
    // Admins CANNOT delete other users' private documents
    const isOwner = 'user_id' in doc && doc.user_id === user.id;
    const isSap = 'source_type' in doc && doc.source_type === 'sap';
    return !isSap && isOwner;
  };
  
  // Note: This component uses DocumentTable and other sub-components
  // that handle API calls through the configured apiClient

  // Load text extraction providers
  const loadTextExtractionProviders = useCallback(async () => {
    try {
      // Note: API client structure might need adjustment based on actual API
      setAvailableProviders([{ value: 'local', label: 'Local' }]);
    } catch (error) {
      log.error('Error loading text extraction providers', { error });
    }
  }, []);

  // Load default text extraction configuration
  const loadDefaultTextExtractionConfig = useCallback(async () => {
    try {
      // Note: API client structure might need adjustment based on actual API
      const provider = 'local';
      setDefaultProvider(provider);
    } catch (error) {
      log.error('Error loading default text extraction config', { error });
    }
  }, []);

  // Server-side filtering via API - no need for client-side filtering
  const filteredDocuments = useMemo(() => {
    // Tab 5 is for Document Sources, return empty array
    if (tabValue === 5) {
      return [];
    }

    // If search is active and has results, convert and show search results
    if (searchResults.length > 0) {return searchResults;}

    return documents;
  }, [documents, tabValue, searchResults]);
  
  // Initial load of providers and config
  useEffect(() => {
    loadTextExtractionProviders();
    loadDefaultTextExtractionConfig();
  }, [loadTextExtractionProviders, loadDefaultTextExtractionConfig]);

  // Clear search results when query becomes empty
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults([]);
    }
  }, [searchQuery]);

  // Handle tab change - reset page when changing tabs
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0); // Reset to first page when changing tabs
  };

  // Handle pagination
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  // Handle filter changes
  const handleVisibilityChange = (value: string) => {
    setVisibility(value);
    setPage(0); // Reset to first page when filter changes
  };

  const handleSortByChange = (value: string) => {
    setSortBy(value);
    setPage(0); // Reset to first page when sort changes
  };

  const handleDateFiltersChange = (filters: DateRangeFilters) => {
    setDateFilters(filters);
    setPage(0); // Reset to first page when date filters change
  };

  const handleSharedWithGroupChange = (groupId: string) => {
    setSharedWithGroup(groupId);
    setPage(0); // Reset to first page when filter changes
  };

  const handleSharedWithUserChange = (userId: string) => {
    setSharedWithUser(userId);
    setPage(0); // Reset to first page when filter changes
  };

  const handleClearFilters = () => {
    setVisibility('all');
    setSortBy('newest');
    setDateFilters({
      createdFrom: null,
      createdTo: null,
      updatedFrom: null,
      updatedTo: null
    });
    setSharedWithGroup('');
    setSharedWithUser('');
    setPage(0);
  };

  // Handle search
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const response = await apiClient.searchDocumentsApiDocumentsSearchPost({
        query: searchQuery,
        limit: 100,
      });
      setSearchResults(response.data.results || []);
    } catch (error) {
      log.error('Error searching documents', { error });
      showError(t('documents.search.failed'));
    } finally {
      setSearching(false);
    }
  };

  // Open document dialog
  const handleOpenDialog = (doc?: Document | SearchResult | null) => {
    if (doc && 'created_at' in doc && 'updated_at' in doc) {
      setEditingDoc(doc as Document);
    } else {
      setEditingDoc(null);
    }
    setOpenDialog(true);
  };

  // Submit document form
  const handleSubmitDocument = async (docData: any, isEdit: boolean) => {
    try {
      if (isEdit && editingDoc) {
        await updateDocumentMutation.mutateAsync({
          documentId: editingDoc.document_id,
          data: docData
        });
        showSuccess(t('documents.notification.updated'));
      } else {
        await createDocumentMutation.mutateAsync(docData);
        showSuccess(t('documents.notification.created'));
      }
    } catch (error) {
      log.error('Error submitting document', { error });
      showError(t('documents.notification.error'));
      throw error;
    }
  };

  // Handle delete document
  const handleDeleteDocument = async () => {
    if (!deleteDocId) {return;}

    try {
      await deleteDocumentMutation.mutateAsync(deleteDocId);
      showSuccess(t('documents.notification.deleteSuccess'));
      setOpenDeleteDialog(false);
    } catch (error) {
      log.error('Error deleting document', { error });
      showError(t('documents.notification.deleteFailed'));
    }
  };

  // Open delete confirmation dialog
  const confirmDelete = (docId: string) => {
    setDeleteDocId(docId);
    setOpenDeleteDialog(true);
  };

  // Open permissions dialog
  const openPermissions = (doc: Document | SearchResult) => {
    setPermissionsDocId(doc.document_id || '');
    setPermissionsDocTitle(doc.title || '');
    setOpenPermissionsDialog(true);
  };

  // Handle permissions dialog close
  const handlePermissionsClose = () => {
    setOpenPermissionsDialog(false);
    setPermissionsDocId('');
    setPermissionsDocTitle('');
  };

  // Handle permissions updated
  const handlePermissionsUpdated = () => {
    refetchDocuments();
    showSuccess(t('documents.permissions.updated'));
  };

  return (
    <>
      <PageLayout
        title={t('documents.title')}
        isLoading={false}
        maxWidth="lg"
      >
        {/* Search Bar */}
        <DocumentSearchBar
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          onSearch={handleSearch}
          onAddDocument={() => handleOpenDialog()}
          searching={searching}
          canAddCustomDocs={canAddCustomDocs}
        />

        {/* Document Tabs */}
        <DocumentTabs tabValue={tabValue} onTabChange={handleTabChange} />

        {/* Filter Bar - only show when not on Document Sources tab */}
        {tabValue !== 5 && (
          <DocumentFilterBar
            visibility={visibility}
            sortBy={sortBy}
            dateFilters={dateFilters}
            sharedWithGroup={sharedWithGroup}
            sharedWithUser={sharedWithUser}
            onVisibilityChange={handleVisibilityChange}
            onSortByChange={handleSortByChange}
            onDateFiltersChange={handleDateFiltersChange}
            onSharedWithGroupChange={handleSharedWithGroupChange}
            onSharedWithUserChange={handleSharedWithUserChange}
            onClearFilters={handleClearFilters}
          />
        )}

        {/* Document List */}
        {tabValue === 5 ? (
          <DocumentSources />
        ) : documentsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <DocumentTable
              documents={filteredDocuments}
              canEditDocument={canEditDocument}
              canDeleteDocument={canDeleteDocument}
              onEdit={handleOpenDialog}
              onDelete={confirmDelete}
              onManagePermissions={openPermissions}
              onShowSnackbar={(message, severity) => {
                switch (severity) {
                  case 'success': showSuccess(message); break;
                  case 'error': showError(message); break;
                  case 'warning': showWarning(message); break;
                  case 'info': showInfo(message); break;
                }
              }}
            />
            {/* Hide pagination when showing search results */}
            {searchResults.length === 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalDocuments}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </Paper>
        )}
      </PageLayout>

      {/* Document Dialog */}
      <DocumentDialog
        open={openDialog}
        onClose={() => { setOpenDialog(false); setEditingDoc(null); }}
        editingDoc={editingDoc}
        onSubmit={handleSubmitDocument}
        availableProviders={availableProviders}
        defaultProvider={defaultProvider}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={handleDeleteDocument}
      />

      {/* Document Permissions Dialog */}
      {permissionsDocId && (
        <DocumentPermissions
          open={openPermissionsDialog}
          onClose={handlePermissionsClose}
          documentId={permissionsDocId}
          documentTitle={permissionsDocTitle}
          onPermissionsUpdated={handlePermissionsUpdated}
        />
      )}
    </>
  );
};

export default Documents;
